// @ts-check

/**
 * Error handling utilities for Anokye API services
 * Parses backend error responses and provides user-friendly messages
 */

import { ErrorCodes } from './chat-provider/errorCodes';

/**
 * HTTP status code to error code mapping
 * Used as fallback when error format is non-standard
 */
const HTTP_STATUS_TO_ERROR_CODE = {
  400: ErrorCodes.INVALID_PARAMETER,
  401: ErrorCodes.INVALID_TOKEN,
  403: ErrorCodes.PERMISSION_DENIED,
  404: ErrorCodes.RESOURCE_NOT_FOUND,
  500: ErrorCodes.INTERNAL_SERVER_ERROR,
  503: ErrorCodes.SERVICE_UNAVAILABLE,
};

/**
 * Map old error codes to new standardized codes
 * Provides backward compatibility with legacy error formats
 * @param {string} oldCode - Legacy error code
 * @returns {string} Standardized error code
 */
function normalizeErrorCode(oldCode) {
  const codeMap = {
    'LOW_CREDIT': ErrorCodes.INSUFFICIENT_FUNDS,
    'LOW_CREDITS': ErrorCodes.INSUFFICIENT_FUNDS,
    // Add other legacy mappings as needed
  };
  return codeMap[oldCode] || oldCode;
}

/**
 * Parse error from Axios/API service response
 * Handles standardized backend error format and legacy formats
 *
 * @param {any} axiosError - Error from Axios request
 * @returns {{code: string, message: string, details: any, timestamp: string, originalError: any}}
 */
export function parseAnokyeError(axiosError) {
  const responseData = axiosError?.response?.data;
  const status = axiosError?.response?.status;

  // Check for standardized backend error format
  // Backend structure: { error: { code, message, details, timestamp } }
  if (responseData?.error) {
    return {
      code: responseData.error.code || ErrorCodes.UNKNOWN_ERROR,
      message: responseData.error.message || 'An error occurred',
      details: responseData.error.details || null,
      timestamp: responseData.error.timestamp || new Date().toISOString(),
      originalError: axiosError
    };
  }

  // Fallback for legacy ERR_CODE format (backward compatibility)
  // Legacy structure: { ERR_CODE: "LOW_CREDIT", message: "..." }
  if (responseData?.ERR_CODE) {
    return {
      code: normalizeErrorCode(responseData.ERR_CODE),
      message: responseData.message || 'An error occurred',
      details: null,
      timestamp: new Date().toISOString(),
      originalError: axiosError
    };
  }

  // Fallback to HTTP status code mapping
  const code = HTTP_STATUS_TO_ERROR_CODE[status] || ErrorCodes.UNKNOWN_ERROR;
  return {
    code,
    message: responseData?.message || axiosError?.message || 'An error occurred',
    details: null,
    timestamp: new Date().toISOString(),
    originalError: axiosError
  };
}

/**
 * Get user-friendly error message based on error code
 * Maps technical error codes to human-readable messages
 *
 * @param {string} errorCode - Error code from ErrorCodes enum
 * @param {string} [defaultMessage] - Fallback message if no mapping exists
 * @returns {string} User-friendly error message
 */
export function getUserFriendlyMessage(errorCode, defaultMessage) {
  const messages = {
    [ErrorCodes.INSUFFICIENT_FUNDS]: 'You do not have enough credits for this action.',
    [ErrorCodes.INVALID_TOKEN]: 'Your session has expired. Please log in again.',
    [ErrorCodes.PERMISSION_DENIED]: 'You do not have permission to perform this action.',
    [ErrorCodes.INSUFFICIENT_PERMISSIONS]: 'You do not have permission to perform this action.',
    [ErrorCodes.RESOURCE_NOT_FOUND]: 'The requested resource could not be found.',
    [ErrorCodes.SESSION_NOT_FOUND]: 'The session could not be found.',
    [ErrorCodes.MESSAGE_NOT_FOUND]: 'The message could not be found.',
    [ErrorCodes.PROJECT_NOT_FOUND]: 'The project could not be found.',
    [ErrorCodes.REPORT_NOT_FOUND]: 'The report could not be found.',
    [ErrorCodes.INVALID_PARAMETER]: 'Some information you provided is invalid.',
    [ErrorCodes.MISSING_PARAMETER]: 'Please provide all required information.',
    // INVALID_OPERATION intentionally omitted - backend provides context-specific messages
    [ErrorCodes.INTERNAL_SERVER_ERROR]: 'Something went wrong. Please try again later.',
    [ErrorCodes.SERVICE_UNAVAILABLE]: 'The service is temporarily unavailable. Please try again later.',
    [ErrorCodes.NETWORK_ERROR]: 'Network error. Please check your connection.',
    [ErrorCodes.REQUEST_TIMEOUT]: 'Request timed out. Please try again.',
    [ErrorCodes.ACTIVE_STREAM_EXISTS]: 'An active stream already exists.',
  };

  return messages[errorCode] || defaultMessage || 'An unexpected error occurred.';
}

/**
 * Check if an error matches a specific error code
 * @param {any} error - Parsed error object
 * @param {string} code - Error code to check
 * @returns {boolean}
 */
export function isErrorCode(error, code) {
  return error?.code === code;
}

// Re-export ErrorCodes for convenience
export { ErrorCodes };
